import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { Patient } from "../../basic-info/calculateBasicInfoCompletion";
import { usePatient } from "@src/packages/patients/hooks/usePatient";

export interface AllergyDetail {
  id?: string;
  allergy?: "pollen" | "dust" | "food" | "animals" | "insect_stings" | "mold" | "medications" | "other" | null;
  name?: string | null;
  other?: string | null;
  comments?: string | null;
  isUnderTreatment?: boolean | null;
  treatmentDescription?: string | null;
}

export interface AllergiesState {
  hasAllergies?: boolean | null;
  allergies: AllergyDetail[];
  allergyIdsToDelete: string[];
}

interface AllergiesContextType {
  state: AllergiesState;
  updateState: (updates: Partial<AllergiesState>) => void;
  resetState: () => void;
}

const AllergiesContext = createContext<AllergiesContextType | undefined>(
  undefined,
);

const initialState: AllergiesState = {
  hasAllergies: null,
  allergies: [],
  allergyIdsToDelete: [],
};

export function AllergiesProvider({ children }: { children: ReactNode }) {
  const { data: patient, isLoading } = usePatient();
  const [state, setState] = useState<AllergiesState>(initialState);

  // Load data from patient (like Basic Info and Infertility)
  useEffect(() => {
    if (isLoading) return;
    if (!patient) return;

    const medicalHistory = patient.medicalHistory;
    if (!medicalHistory) return;

    setState((prev) => {
      // Check if state is in initial empty state
      const isInitialState =
        prev.hasAllergies === null && prev.allergies.length === 0;

      const next: Partial<AllergiesState> = {};

      // Load hasAllergies (only if empty or initial state)
      if (
        (isInitialState || prev.hasAllergies === null) &&
        medicalHistory.hasAllergies !== undefined &&
        medicalHistory.hasAllergies !== null
      ) {
        next.hasAllergies = medicalHistory.hasAllergies;
      }

      // Load allergies (only if empty or initial state)
      if (
        (isInitialState || prev.allergies.length === 0) &&
        medicalHistory.allergies &&
        Array.isArray(medicalHistory.allergies) &&
        medicalHistory.allergies.length > 0
      ) {
        next.allergies = medicalHistory.allergies.map((a: any) => ({
          id: a.id,
          allergy: a.allergy || null,
          name: a.name || null,
          other: a.other || null,
          comments: a.comments || null,
          isUnderTreatment: a.isUnderTreatment || null,
          treatmentDescription: a.treatmentDescription || null,
        }));
      }

      // Only update if there are changes
      if (Object.keys(next).length === 0) {
        return prev;
      }

      return { ...prev, ...next };
    });
  }, [patient, isLoading]);

  const updateState = useCallback((updates: Partial<AllergiesState>) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  return (
    <AllergiesContext.Provider value={{ state, updateState, resetState }}>
      {children}
    </AllergiesContext.Provider>
  );
}

export function useAllergies() {
  const context = useContext(AllergiesContext);
  if (context === undefined) {
    throw new Error(
      "useAllergies must be used within an AllergiesProvider",
    );
  }
  return context;
}
